import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { LookupService } from '../../../../services/lookup.service';
import { UserService } from '../../../../services/user.service';
import { CommonModule, DatePipe } from '@angular/common';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';

@Component({
  selector: 'ppl-claim-adjustment-filter',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    NzGridModule,
    NzSelectModule,
    NzInputModule,
    ButtonComponent,
    DropdownSelectComponent,
    NzDatePickerModule
  ],
  templateUrl: './claim-adjustment-filter.component.html',
  styleUrl: './claim-adjustment-filter.component.css',
})
export class ClaimAdjustmentFilterComponent implements OnInit {
  @Input() filterFields: any[] = [];
  @Input() adjustType = 'single';
  @Output() searchEvent = new EventEmitter<any>();
  @Output() refreshEvent = new EventEmitter<any>();
  showFilterError = false;
  claimFilterForm!: FormGroup;
  programName: any;
  mcoEntries: any[] = [];
  timeframeOptions = [
    { label: 'Last 30 days', value: '30 days' },
    { label: 'Last 60 days', value: '60 days' },
    { label: 'Last 90 days', value: '90 days' },
    { label: 'Last 120 days', value: '120 days' },
    { label: 'Last 150 days', value: '150 days' },
    { label: 'Last 180 days', value: '180 days' },
  ];
  fileTypeList = [
    { label: ' MCO Adjustment', value: 'ClaimMCOAdjustment' },
    { label: ' Service Adjustment', value: 'ClaimServiceAdjustment' },
  ];
  serviceCodes: any;

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    private userService: UserService,
    private datePipe: DatePipe
  ) {}

  ngOnInit() {
    const formFields = this.filterFields.reduce((acc, f) => {
      if (f.show) {
        acc = { ...acc, [f.field]: [f.default ? f.default : null] };
      }
      return acc;
    }, {});
    this.claimFilterForm = this.fb.group(formFields);
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.getDropdownData();
  }

  getDropdownData() {
    this.serviceCodes = this.lookupService.getLookupValue(
      'timesheet_filter_serviceCode'
    );
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status == 200) {
        this.mcoEntries = data.responsedata.filter(
          (item: any) => item.orgTypeLookup === 'MCO'
        );
      }
    });
  }

  search() {
    const formData = this.claimFilterForm.value;
    if (
      !formData.participantPplId &&
      !formData.pplIcn &&
      this.adjustType === 'single'
    ) {
      this.showFilterError = true;
    } else {
      this.showFilterError = false;
      const filters = this.getFilters(formData);
      this.searchEvent.emit(filters);
    }
  }

  refresh() {
        const formData = this.claimFilterForm.value;
    if (
      !formData.participantPplId &&
      !formData.pplIcn &&
      this.adjustType === 'single'
    ) {
      this.showFilterError = true;
    } else {
      this.showFilterError = false;
      const filters = this.getFilters(formData);
      this.refreshEvent.emit(filters);
    }
  }

  getDate(timeframe: string) {
    const days = parseInt(timeframe.split(' ')[0], 10);
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - days);
    const formattedDate = `${(currentDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${currentDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${currentDate.getFullYear()}`;
    return formattedDate;
  }

  getFilters(data: any) {
    const filters = [];

    if (data.timeframe) {
      filters.push({
        jsonPath: 'createdDate',
        value: this.getDate(data.timeframe),
        operator: 'greaterthan',
      });
    }

    if (data.serviceDate) {
      filters.push({
        jsonPath: 'serviceDate',
        value: this.datePipe.transform(data.serviceDate, 'MM/dd/yyyy'),
        operator: 'Equal',
      });
    }

    if (data.pplIcn && data.pplIcn.trim()) {
      filters.push({
        jsonPath: 'pplIcn',
        value: data.pplIcn.trim(),
        operator: 'Equal',
      });
    }

    if (data.participantPplId && data.participantPplId.trim()) {
      filters.push({
        jsonPath: 'participantPplId',
        value: data.participantPplId.trim(),
        operator: 'Equal',
      });
    }

    if (data.serviceCode && data.serviceCode.trim()) {
      filters.push({
        jsonPath: 'serviceCodeDetailId',
        value: data.serviceCode.trim(),
        operator: 'Equal',
      });
    }
    if (data.modifierCode && data.modifierCode.trim()) {
      filters.push({
        jsonPath: 'modifierCode',
        value: data.modifierCode.trim(),
        operator: 'Equal',
      });
    }
    if (data.authNumber && data.authNumber.trim()) {
      filters.push({
        jsonPath: 'authNumber',
        value: data.authNumber.trim(),
        operator: 'Equal',
      });
    }
    if (data.mco && data.mco.trim()) {
      filters.push({
        jsonPath: 'mcoName',
        value: data.mco,
        operator: 'Equal',
      });
    }
    if (data.batchFileTypeLkp) {
      filters.push({
        jsonPath: 'batchFileTypeLkp',
        value: data.batchFileTypeLkp,
        operator: 'Equal',
      });
    }

    return filters;
  }
}
